class GiftBuilder {
  constructor(section) {
    this.section = section;
    this.packaging = null;
    this.greetingCard = null;
    this.items = new Map();

    this.searchTerm = '';
    this.searchTimer = null;
    this.searching = false;
    this.searchPage = 1;
    this.searchDone = false;
    this.searchResults = [];
    this.searchSeenHandles = new Set();
    this.searchBatchSize = 24;

    this.loading = false;
    this.normalGridHtml = '';
    this.moneyFormat = section.dataset.moneyFormat || '';

    this.excludedProductHandles = new Set([
      'cargo-gift-bag-extra-small',
      'cargo-gift-bag-small',
      'cargo-gift-bag-large',
      'cargo-wine-gift-bag',
      'cargo-gift-bag-wine',
      'cargo-gift-box'
    ]);

    this.excludedTerms = [
      'decantas',
      'oak & steel',
      'oak and steel',
      'mikamax'
    ];

    this.cacheElements();
    this.bindEvents();
    this.captureNormalGrid();
    this.restoreVisibleQuantities();
    this.applyLocalSearch();
    this.render();
  }

  cacheElements() {
    this.summaryPackaging = this.section.querySelector('[data-summary-packaging]');
    this.summaryItems = this.section.querySelector('[data-summary-items]');
    this.summaryGreetingCard = this.section.querySelector('[data-summary-greeting-card]');
    this.summaryMessage = this.section.querySelector('[data-summary-message]');
    this.productsSubtotal = this.section.querySelector('[data-products-subtotal]');
    this.packagingTotal = this.section.querySelector('[data-packaging-total]');
    this.greetingCardTotal = this.section.querySelector('[data-greeting-card-total]');
    this.grandTotal = this.section.querySelector('[data-grand-total]');
    this.addButton = this.section.querySelector('[data-gb-add-to-cart]');
    this.note = this.section.querySelector('[data-gb-cart-note]');
    this.message = this.section.querySelector('[data-gb-message]');
    this.search = this.section.querySelector('[data-gb-search]');
  }

  get grid() {
    return this.section.querySelector('[data-gb-product-grid]');
  }

  get productCards() {
    return Array.from(this.section.querySelectorAll('[data-product-card]'));
  }

  get categoryButtons() {
    return Array.from(this.section.querySelectorAll('[data-category-filter]'));
  }

  bindEvents() {
    this.section.addEventListener('click', (event) => {
      const packagingButton = event.target.closest('[data-packaging]');
      const greetingButton = event.target.closest('[data-greeting-card]');
      const productPlusButton = event.target.closest('[data-qty-plus]');
      const productMinusButton = event.target.closest('[data-qty-minus]');
      const summaryPlusButton = event.target.closest('[data-summary-plus]');
      const summaryMinusButton = event.target.closest('[data-summary-minus]');
      const removeButton = event.target.closest('[data-remove-item]');
      const categoryButton = event.target.closest('[data-category-filter]');
      const loadMoreButton = event.target.closest('[data-gb-load-more]');
      const searchMoreButton = event.target.closest('[data-gb-search-more]');
      const addButton = event.target.closest('[data-gb-add-to-cart]');

      if (packagingButton) {
        this.selectPackaging(packagingButton);
        return;
      }

      if (greetingButton) {
        this.selectGreetingCard(greetingButton);
        return;
      }

      if (productPlusButton) {
        this.changeProductQuantity(productPlusButton, 1);
        return;
      }

      if (productMinusButton) {
        this.changeProductQuantity(productMinusButton, -1);
        return;
      }

      if (summaryPlusButton) {
        this.changeSelectedItemQuantity(summaryPlusButton.dataset.summaryPlus, 1);
        return;
      }

      if (summaryMinusButton) {
        this.changeSelectedItemQuantity(summaryMinusButton.dataset.summaryMinus, -1);
        return;
      }

      if (removeButton) {
        this.removeItem(removeButton.dataset.removeItem);
        return;
      }

      if (categoryButton) {
        this.loadCategory(categoryButton);
        return;
      }

      if (searchMoreButton) {
        this.loadMoreSearchResults();
        return;
      }

      if (loadMoreButton) {
        this.loadMore(loadMoreButton);
        return;
      }

      if (addButton) {
        this.addToCart();
      }
    });

    if (this.search) {
      this.search.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);

        this.searchTimer = window.setTimeout(() => {
          this.searchTerm = this.normalizeSearch(this.search.value);
          this.handleSearch();
        }, 300);
      });
    }

    if (this.message) {
      this.message.addEventListener('input', () => {
        this.renderGiftMessage();
      });
    }
  }

  captureNormalGrid() {
    if (!this.grid || this.searchTerm) return;
    this.normalGridHtml = this.grid.innerHTML;
  }

  async handleSearch() {
    if (!this.grid) return;

    if (!this.searchTerm) {
      if (this.normalGridHtml) {
        this.grid.innerHTML = this.normalGridHtml;
      }

      this.restoreVisibleQuantities();
      this.applyLocalSearch();
      return;
    }

    if (this.searchTerm.length < 2) {
      this.applyLocalSearch();
      return;
    }

    await this.startGlobalSearch();
  }

  async startGlobalSearch() {
    this.searchPage = 1;
    this.searchDone = false;
    this.searchResults = [];
    this.searchSeenHandles = new Set();

    if (this.grid) {
      this.grid.innerHTML = '<p class="gb-empty">Searching products...</p>';
    }

    await this.loadMoreSearchResults();
  }

  async loadMoreSearchResults() {
    if (this.searching || this.searchDone || !this.searchTerm || !this.grid) return;

    this.searching = true;
    const targetCount = this.searchResults.length + this.searchBatchSize;

    try {
      while (this.searchResults.length < targetCount && !this.searchDone) {
        const response = await fetch(`/products.json?limit=250&page=${this.searchPage}`, {
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Product search failed');

        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];

        if (products.length === 0) {
          this.searchDone = true;
          break;
        }

        products.forEach((product) => {
          const handle = this.normalizeSearch(product.handle);

          if (!handle || this.searchSeenHandles.has(handle)) return;
          this.searchSeenHandles.add(handle);

          if (!this.isSelectableProduct(product)) return;
          if (!this.productMatchesSearch(product, this.searchTerm)) return;

          this.searchResults.push(product);
        });

        this.searchPage += 1;
      }

      this.renderSearchResults(this.searchResults);
      this.restoreVisibleQuantities();
      this.applyLocalSearch();
    } catch (error) {
      this.grid.innerHTML = '<p class="gb-empty" data-gb-no-results>No products found.</p>';
    } finally {
      this.searching = false;
      this.render();
    }
  }

  productMatchesSearch(product, term) {
    const title = this.normalizeSearch(product.title);
    const vendor = this.normalizeSearch(product.vendor);

    return title.includes(term) || vendor.includes(term);
  }

  isSelectableProduct(product) {
    const handle = this.normalizeSearch(product.handle);
    const title = this.normalizeSearch(product.title);
    const vendor = this.normalizeSearch(product.vendor);
    const availableVariant = this.getAvailableVariant(product);

    if (!availableVariant) return false;
    if (this.excludedProductHandles.has(handle)) return false;

    return !this.excludedTerms.some((term) => {
      return title.includes(term) || vendor.includes(term) || handle.includes(term.replace(/\s+/g, '-'));
    });
  }

  getAvailableVariant(product) {
    if (!Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available) || null;
  }

  getProductImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;

    if (Array.isArray(product.images) && product.images[0]) {
      return product.images[0].src || product.images[0];
    }

    return '';
  }

  priceToCents(value) {
    if (typeof value === 'string' && value.includes('.')) {
      return Math.round(Number(value) * 100);
    }

    return Number(value || 0);
  }

  renderSearchResults(products) {
    if (!this.grid) return;

    if (products.length === 0) {
      this.grid.innerHTML = '<p class="gb-empty" data-gb-no-results>No products found.</p>';
      return;
    }

    const groupedProducts = products.reduce((groups, product) => {
      const vendor = product.vendor || 'Gourmet Boutique';
      const key = this.handleize(vendor);

      if (!groups[key]) {
        groups[key] = {
          title: vendor,
          products: []
        };
      }

      groups[key].products.push(product);
      return groups;
    }, {});

    const groupsHtml = Object.keys(groupedProducts).map((key) => {
      const group = groupedProducts[key];

      return `
        <details class="gb-vendor-accordion" data-vendor-group="${this.escapeHtml(key)}" open>
          <summary>
            <span>${this.escapeHtml(group.title)}</span>
            <span class="gb-vendor-icon" aria-hidden="true"></span>
          </summary>

          <div class="gb-card-grid" data-vendor-products>
            ${group.products.map((product) => this.renderProductCard(product)).join('')}
          </div>
        </details>
      `;
    }).join('');

    const moreButton = this.searchDone
      ? ''
      : '<button type="button" class="gb-load-more" data-gb-search-more>View More</button>';

    this.grid.innerHTML = `
      <div class="gb-vendor-list" data-vendor-list>
        ${groupsHtml}
      </div>
      ${moreButton}
    `;
  }

  renderProductCard(product) {
    const variant = this.getAvailableVariant(product);
    const image = this.getProductImage(product);
    const price = this.priceToCents(variant.price || product.price || 0);
    const productUrl = product.url || `/products/${product.handle}`;

    return `
      <article
        class="gb-product-card"
        data-product-card
        data-title="${this.escapeHtml(this.normalizeSearch(product.title))}"
        data-vendor="${this.escapeHtml(this.normalizeSearch(product.vendor || ''))}"
        data-categories=""
        data-id="${variant.id}"
        data-price="${price}"
        data-product-title="${this.escapeHtml(product.title)}"
        data-image="${this.escapeHtml(image || '')}"
      >
        <a class="gb-product-link" href="${this.escapeHtml(productUrl)}">
          <div class="gb-product-image">
            ${image ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>

          <h3>${this.escapeHtml(product.title)}</h3>
        </a>

        <div class="gb-product-info">
          <p>${this.formatMoney(price)}</p>

          <div class="gb-qty-control" data-qty-control>
            <button type="button" data-qty-minus aria-label="Decrease ${this.escapeHtml(product.title)}">-</button>
            <span data-qty-value>0</span>
            <button type="button" data-qty-plus aria-label="Increase ${this.escapeHtml(product.title)}">+</button>
          </div>
        </div>
      </article>
    `;
  }

  selectPackaging(button) {
    const variantId = Number(button.dataset.id);

    if (!variantId) {
      this.setNote('Packaging is currently unavailable.');
      return;
    }

    this.section.querySelectorAll('[data-packaging]').forEach((card) => {
      const selected = card === button;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.packaging = {
      id: variantId,
      title: button.dataset.title || 'Packaging',
      size: button.dataset.size || '',
      price: Number(button.dataset.price || 0),
      image: button.dataset.image || ''
    };

    this.setNote('');
    this.render();
  }

  selectGreetingCard(button) {
    const alreadySelected = this.greetingCard && String(this.greetingCard.id) === String(button.dataset.id);

    this.section.querySelectorAll('[data-greeting-card]').forEach((card) => {
      card.classList.remove('is-selected');
      card.setAttribute('aria-pressed', 'false');
    });

    if (alreadySelected) {
      this.greetingCard = null;
    } else {
      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');

      this.greetingCard = {
        id: Number(button.dataset.id),
        title: button.dataset.title || 'Greeting Card',
        price: Number(button.dataset.price || 0),
        image: button.dataset.image || ''
      };
    }

    this.setNote('');
    this.render();
  }

  changeProductQuantity(button, change) {
    const card = button.closest('[data-product-card]');
    if (!card || !card.dataset.id) return;

    const id = String(card.dataset.id);
    const current = this.items.get(id);
    const nextQuantity = Math.max(0, (current ? current.quantity : 0) + change);

    if (nextQuantity === 0) {
      this.items.delete(id);
    } else {
      this.items.set(id, {
        id: Number(id),
        title: card.dataset.productTitle || 'Gift item',
        price: Number(card.dataset.price || 0),
        image: card.dataset.image || '',
        quantity: nextQuantity
      });
    }

    this.syncProductCardQuantity(id, nextQuantity);
    this.setNote('');
    this.render();
  }

  changeSelectedItemQuantity(id, change) {
    const key = String(id);
    const current = this.items.get(key);
    if (!current) return;

    const nextQuantity = Math.max(0, current.quantity + change);

    if (nextQuantity === 0) {
      this.items.delete(key);
    } else {
      current.quantity = nextQuantity;
      this.items.set(key, current);
    }

    this.syncProductCardQuantity(key, nextQuantity);
    this.setNote('');
    this.render();
  }

  removeItem(id) {
    const key = String(id);
    this.items.delete(key);
    this.syncProductCardQuantity(key, 0);
    this.setNote('');
    this.render();
  }

  async loadCategory(button) {
    const url = button.dataset.categoryUrl;
    if (this.loading || !url) return;

    this.loading = true;
    this.setNote('');

    this.categoryButtons.forEach((categoryButton) => {
      categoryButton.classList.toggle('is-active', categoryButton === button);
    });

    try {
      const nextSection = await this.fetchSection(url);
      const nextGrid = nextSection.querySelector('[data-gb-product-grid]');

      if (!nextGrid || !this.grid) {
        throw new Error('Product grid was not found.');
      }

      this.grid.innerHTML = nextGrid.innerHTML;
      this.searchTerm = this.search ? this.normalizeSearch(this.search.value) : '';

      if (!this.searchTerm) {
        this.captureNormalGrid();
      }

      this.restoreVisibleQuantities();

      if (this.searchTerm.length >= 2) {
        await this.startGlobalSearch();
      } else {
        this.applyLocalSearch();
      }
    } catch (error) {
      this.setNote('Products could not be loaded. Please refresh and try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async loadMore(button) {
    const url = button.dataset.nextUrl;
    if (this.loading || !url || this.searchTerm) return;

    this.loading = true;
    button.disabled = true;
    button.textContent = 'Loading...';

    try {
      const nextSection = await this.fetchSection(url);
      const nextGrid = nextSection.querySelector('[data-gb-product-grid]');

      if (!nextGrid || !this.grid) {
        throw new Error('More products were not found.');
      }

      this.appendLoadedProducts(nextGrid, button);

      const nextButton = nextGrid.querySelector('[data-gb-load-more]');
      if (nextButton && nextButton.dataset.nextUrl) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl;
        button.disabled = false;
        button.textContent = 'Load More';
      } else {
        button.remove();
      }

      this.captureNormalGrid();
      this.restoreVisibleQuantities();
      this.applyLocalSearch();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try Again';
      this.setNote('More products could not be loaded. Please try again.');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  appendLoadedProducts(nextGrid, currentLoadMoreButton) {
    const currentVendorList = this.grid.querySelector('[data-vendor-list]');
    const nextVendorList = nextGrid.querySelector('[data-vendor-list]');
    const currentPremiumSection = this.grid.querySelector('[data-product-section="premium"]');

    if (!nextVendorList) return;

    if (!currentVendorList) {
      this.grid.insertBefore(nextVendorList.cloneNode(true), currentPremiumSection || currentLoadMoreButton);
      this.removeDuplicateProductCards();
      return;
    }

    nextVendorList.querySelectorAll('[data-vendor-group]').forEach((nextGroup) => {
      const groupKey = nextGroup.dataset.vendorGroup;
      let currentGroup = currentVendorList.querySelector(`[data-vendor-group="${this.escapeSelector(groupKey)}"]`);

      if (!currentGroup) {
        currentVendorList.appendChild(nextGroup.cloneNode(true));
        return;
      }

      const currentProducts = currentGroup.querySelector('[data-vendor-products]');
      const nextProducts = nextGroup.querySelector('[data-vendor-products]');

      if (!currentProducts || !nextProducts) return;

      nextProducts.querySelectorAll('[data-product-card]').forEach((nextCard) => {
        const id = nextCard.dataset.id;
        const existing = this.grid.querySelector(`[data-product-card][data-id="${this.escapeSelector(id)}"]`);

        if (existing) return;

        currentProducts.appendChild(nextCard.cloneNode(true));
      });
    });

    this.removeDuplicateProductCards();
  }

  removeDuplicateProductCards() {
    const seen = new Set();

    this.productCards.forEach((card) => {
      const id = String(card.dataset.id || '');
      if (!id) return;

      if (seen.has(id)) {
        card.remove();
        return;
      }

      seen.add(id);
    });
  }

  async fetchSection(url) {
    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error('Section request failed.');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = doc.querySelector(`#${this.section.id}`);

    if (!nextSection) {
      throw new Error('Gift Builder section was not found in response.');
    }

    return nextSection;
  }

  normalizeSearch(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  applyLocalSearch() {
    const term = this.searchTerm;
    let visibleProducts = 0;

    this.productCards.forEach((card) => {
      const title = this.normalizeSearch(card.dataset.title || card.dataset.productTitle || '');
      const vendor = this.normalizeSearch(card.dataset.vendor || '');
      const matches = !term || title.includes(term) || vendor.includes(term);

      card.classList.toggle('is-hidden', !matches);

      if (matches) {
        visibleProducts += 1;
      }
    });

    this.updateGroupVisibility();
    this.updateNoResultsMessage(visibleProducts);
  }

  updateGroupVisibility() {
    this.section.querySelectorAll('[data-vendor-group]').forEach((group) => {
      const hasVisibleProduct = Array.from(group.querySelectorAll('[data-product-card]')).some((card) => {
        return !card.classList.contains('is-hidden');
      });

      group.hidden = !hasVisibleProduct;
    });

    this.section.querySelectorAll('[data-vendor-list]').forEach((vendorList) => {
      const hasVisibleGroup = Array.from(vendorList.querySelectorAll('[data-vendor-group]')).some((group) => {
        return !group.hidden;
      });

      vendorList.hidden = !hasVisibleGroup;
    });

    this.section.querySelectorAll('[data-product-section]').forEach((productSection) => {
      const hasVisibleProduct = Array.from(productSection.querySelectorAll('[data-product-card]')).some((card) => {
        return !card.classList.contains('is-hidden');
      });

      productSection.hidden = !hasVisibleProduct;
    });
  }

  updateNoResultsMessage(visibleProducts) {
    if (!this.grid) return;

    let message = this.grid.querySelector('[data-gb-no-results]');

    if (visibleProducts > 0) {
      if (message) message.hidden = true;
      return;
    }

    if (!message) {
      message = document.createElement('p');
      message.className = 'gb-empty';
      message.setAttribute('data-gb-no-results', '');
      message.textContent = 'No products found.';
      this.grid.appendChild(message);
    }

    message.hidden = false;
  }

  restoreVisibleQuantities() {
    this.productCards.forEach((card) => {
      const quantity = this.items.get(String(card.dataset.id))?.quantity || 0;
      const value = card.querySelector('[data-qty-value]');

      if (value) {
        value.textContent = String(quantity);
      }
    });
  }

  syncProductCardQuantity(id, quantity) {
    this.section.querySelectorAll(`[data-product-card][data-id="${this.escapeSelector(String(id))}"] [data-qty-value]`).forEach((value) => {
      value.textContent = String(quantity);
    });
  }

  render() {
    this.renderPackaging();
    this.renderItems();
    this.renderGreetingCard();
    this.renderGiftMessage();
    this.renderTotals();

    if (this.addButton) {
      this.addButton.disabled = !this.packaging || this.items.size === 0 || this.loading;
    }
  }

  renderPackaging() {
    if (!this.summaryPackaging) return;

    if (!this.packaging) {
      this.summaryPackaging.innerHTML = '<p>No packaging selected yet.</p>';
      return;
    }

    const size = this.packaging.size ? `<span>${this.escapeHtml(this.packaging.size)}</span>` : '';

    this.summaryPackaging.innerHTML = `
      <div class="gb-summary-packaging-card">
        ${this.packaging.image ? `<img src="${this.escapeHtml(this.packaging.image)}" alt="">` : ''}
        <div>
          <strong>${this.escapeHtml(this.packaging.title)}</strong>
          ${size}
          <span>${this.formatMoney(this.packaging.price)}</span>
        </div>
      </div>
    `;
  }

  renderItems() {
    if (!this.summaryItems) return;

    if (this.items.size === 0) {
      this.summaryItems.innerHTML = '<p class="gb-empty">Choose products to begin building your gift.</p>';
      return;
    }

    this.summaryItems.innerHTML = Array.from(this.items.values()).map((item) => {
      return `
        <div class="gb-summary-item" data-summary-item="${item.id}">
          ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="">` : ''}
          <div>
            <strong>${this.escapeHtml(item.title)}</strong>
            <span>${this.formatMoney(item.price)} x ${item.quantity}</span>

            <div class="gb-summary-actions">
              <div class="gb-qty-control">
                <button type="button" data-summary-minus="${item.id}" aria-label="Decrease ${this.escapeHtml(item.title)}">-</button>
                <span>${item.quantity}</span>
                <button type="button" data-summary-plus="${item.id}" aria-label="Increase ${this.escapeHtml(item.title)}">+</button>
              </div>

              <button type="button" class="gb-remove" data-remove-item="${item.id}">Remove</button>
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  renderGreetingCard() {
    if (!this.summaryGreetingCard) return;

    if (!this.greetingCard) {
      this.summaryGreetingCard.innerHTML = '<strong>Greeting Card</strong><p>No greeting card selected.</p>';
      return;
    }

    this.summaryGreetingCard.innerHTML = `
      <strong>Greeting Card</strong>
      <div class="gb-summary-packaging-card">
        ${this.greetingCard.image ? `<img src="${this.escapeHtml(this.greetingCard.image)}" alt="">` : ''}
        <div>
          <strong>${this.escapeHtml(this.greetingCard.title)}</strong>
          <span>${this.formatMoney(this.greetingCard.price)}</span>
        </div>
      </div>
    `;
  }

  renderGiftMessage() {
    if (!this.summaryMessage) return;

    const giftMessage = this.message ? this.message.value.trim() : '';

    this.summaryMessage.innerHTML = `
      <strong>Gift Message</strong>
      <p>${giftMessage ? this.escapeHtml(giftMessage) : 'No message added yet.'}</p>
    `;
  }

  renderTotals() {
    const productsTotal = Array.from(this.items.values()).reduce((sum, item) => {
      return sum + item.price * item.quantity;
    }, 0);

    const packagingTotal = this.packaging ? this.packaging.price : 0;
    const greetingTotal = this.greetingCard ? this.greetingCard.price : 0;
    const grandTotal = productsTotal + packagingTotal + greetingTotal;

    if (this.productsSubtotal) this.productsSubtotal.textContent = this.formatMoney(productsTotal);
    if (this.packagingTotal) this.packagingTotal.textContent = this.formatMoney(packagingTotal);
    if (this.greetingCardTotal) this.greetingCardTotal.textContent = this.formatMoney(greetingTotal);
    if (this.grandTotal) this.grandTotal.textContent = this.formatMoney(grandTotal);
  }

  async addToCart() {
    if (this.loading) return;

    if (!this.packaging) {
      this.setNote('Please choose packaging first.');
      return;
    }

    if (this.items.size === 0) {
      this.setNote('Please choose at least one product for the gift.');
      return;
    }

    this.loading = true;
    this.setNote('');

    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Adding...';
    }

    const giftMessage = this.message ? this.message.value.trim() : '';
    const properties = giftMessage ? { 'Gift Message': giftMessage } : {};

    const cartItems = [
      {
        id: this.packaging.id,
        quantity: 1,
        properties: {
          '_Gift Builder Item': 'Packaging',
          ...properties
        }
      },
      ...Array.from(this.items.values()).map((item) => ({
        id: item.id,
        quantity: item.quantity,
        properties: {
          '_Gift Builder Item': 'Product',
          ...properties
        }
      }))
    ];

    if (this.greetingCard) {
      cartItems.push({
        id: this.greetingCard.id,
        quantity: 1,
        properties: {
          '_Gift Builder Item': 'Greeting Card',
          ...properties
        }
      });
    }

    try {
      await this.saveCartAttributes(giftMessage);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) throw new Error('Cart add failed');

      window.location.href = this.section.dataset.cartRedirect === 'checkout' ? '/checkout' : '/cart';
    } catch (error) {
      this.loading = false;

      if (this.addButton) {
        this.addButton.disabled = false;
        this.addButton.textContent = 'Add Gift To Cart';
      }

      this.setNote('We could not add your gift yet. Please check availability and try again.');
    }
  }

  async saveCartAttributes(giftMessage) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'Gift Message': giftMessage || ''
        }
      })
    });

    if (!response.ok) throw new Error('Cart attributes failed');
  }

  setNote(message) {
    if (this.note) this.note.textContent = message;
  }

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'EUR';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency
    }).format(Number(cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character];
    });
  }

  escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value || '').replace(/["\\]/g, '\\$&');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.gift-builder').forEach((section) => {
    if (section.giftBuilderInstance) return;
    section.giftBuilderInstance = new GiftBuilder(section);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.gift-builder');

  if (section) {
    section.giftBuilderInstance = new GiftBuilder(section);
  }
});